import { useParams, Link, Navigate } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { SERVICES } from "@/lib/services";
import RichText from "@/components/RichText";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t } = useContent();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const otherServices = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`service-detail-${slug}`}>
      {/* HERO */}
      <section className="relative bg-ink-800 text-bone-50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${service.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-800 via-ink-800/85 to-ink-800/30" />
        <div className="relative section pt-24 pb-20 md:pt-32 md:pb-28">
          <div className="text-xs uppercase tracking-[0.28em] text-bone-100/60 mb-6">
            <Link to="/services" className="hover:text-gold-400">Services</Link>
            <span className="mx-3 text-gold-500">/</span>
            <span className="text-gold-400">{t(`service.${slug}.title`)}</span>
          </div>
          <div className="eyebrow !text-gold-400 mb-6">{t(`service.${slug}.eyebrow`)}</div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight max-w-4xl">
            {t(`service.${slug}.headline`)}
          </h1>
          <i className={`${service.icon} text-5xl text-gold-500 mt-12`} />
        </div>
      </section>

      {/* INTRO + BODY */}
      <section className="section py-20 md:py-28 grid md:grid-cols-12 gap-14">
        <aside className="md:col-span-4">
          <div className="md:sticky md:top-32 border-t border-gold-500 pt-8">
            <div className="eyebrow mb-6">Need this service?</div>
            <p className="font-serif text-xl text-ink-800 leading-snug">
              Speak directly with our team about your case.
            </p>
            <Link to="/contact" className="btn-outline-ink mt-8" data-testid="service-cta">
              Request consultation
            </Link>
            <div className="hairline-divider my-10" />
            <div className="eyebrow mb-6">Other services</div>
            <ul className="space-y-3">
              {otherServices.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-ink-800/80 hover:text-gold-600 text-sm flex items-center gap-2"
                    data-testid={`related-service-${s.slug}`}
                  >
                    <i className={`${s.icon} text-gold-600 w-5`} />
                    {t(`service.${s.slug}.title`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <article className="md:col-span-8 max-w-3xl">
          <div className="text-xl md:text-2xl font-serif text-ink-800 leading-[1.5] mb-12 font-light">
            <RichText text={t(`service.${slug}.intro`)} testId="service-intro" />
          </div>
          <div className="hairline-divider mb-12" />
          <RichText text={t(`service.${slug}.body`)} testId="service-body" />

          <div className="mt-16 bg-ink-800 text-bone-50 p-10">
            <h3 className="font-serif text-2xl mb-3">Ready to start?</h3>
            <p className="text-bone-100/80 mb-6 max-w-xl">
              Send a short message and we will respond within one business day.
            </p>
            <Link to="/contact" className="btn-gold" data-testid="service-bottom-cta">
              Contact our team <i className="fa-solid fa-arrow-right text-[10px]" />
            </Link>
          </div>
        </article>
      </section>
    </div>
  );
}
